/**
 * PageHeader — simple page header with title, subtitle and optional actions.
 */
export function PageHeader({ title, subtitle, titleKey, subtitleKey, actions, testId }) {
  return (
    <header
      data-testid={testId || "page-header"}
      className="px-4 md:px-8 py-4 md:py-6 border-b border-neutral-200 bg-white flex flex-wrap items-start justify-between gap-3 md:gap-4"
    >
      <div>
        <div className="label-caps mb-1">{subtitle}</div>
        <h1 className="font-display font-black text-2xl md:text-3xl lg:text-4xl tracking-tighter text-neutral-900">
          {title}
        </h1>
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </header>
  );
}

// Color palette matching the Excel data-validation pills for Jobs Pipeline
const STATUS_STYLES = {
  "To be planned":     "bg-amber-100 text-amber-900 border border-amber-200",
  "Converted BPR":     "bg-violet-100 text-violet-900 border border-violet-200",
  "Hold":              "bg-rose-700 text-white",
  "Cancelled":         "bg-rose-100 text-rose-900 border border-rose-200",
  "Enquiry":           "bg-emerald-100 text-emerald-900 border border-emerald-200",
  "Dispatched":        "bg-emerald-700 text-white",
  "Only Shade card":   "bg-teal-100 text-teal-900 border border-teal-200",
  "Outsource":         "bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-200",
  "Inks":              "bg-sky-100 text-sky-900 border border-sky-200",
  "New Development":   "bg-pink-100 text-pink-900 border border-pink-200",
  "Part qty":          "bg-neutral-200 text-neutral-800",
  "RIBBON":            "bg-neutral-100 text-neutral-800 border border-neutral-300",
  "Sample":            "bg-rose-200 text-rose-900",
  "OPEN PO":           "bg-neutral-100 text-neutral-800 border border-neutral-300",
};

/** Marks a partly printed job's lot ("PART 1") or the original job that has lots out ("SPLIT"). */
export function LotTag({ job }) {
  if (!job || !(job.is_lot || job.lot_count)) return null;
  return (
    <span className="inline-block px-1.5 py-0.5 rounded-sm border border-amber-400 bg-amber-100 text-amber-900 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap"
      title={job.is_lot ? `Printed part of ${job.parent_mpwo}` : `${job.lot_count} printed part(s) passed on`} data-testid="lot-tag">
      {job.is_lot ? `Part ${job.lot_no}` : "Split"}
    </span>
  );
}

// A completed stage's status_key names the stage that just finished, not the one a job is waiting
// on next — "printing" means Printing is done and AVT is next, which reads as "the job is being
// printed" to anyone not holding the status_key -> next-stage mapping in their head. Shown as what
// it actually means instead; the raw value still drives the badge's color/testid, unchanged.
// The Jobs Pipeline's own statuses (To be planned, Converted BPR, ...) are shown as the sheet has them.
const READY_FOR_LABELS = {
  "": "Ready for Stores", pending: "Ready for Stores",
  in_production: "Ready for Printing",
  printing: "Ready for AVT", flat_bed: "Ready for AVT",
  avt: "Ready for Slitting",
  slitting: "Ready for QC",
  qc: "Ready for Packing",
  packing: "Ready for COA",
  ready_to_dispatch: "Ready for Dispatch",
};

// The app's own statuses for each department (job_status.py ROLE_READY_STATUSES, kept in sync by hand).
// A job still marked Converted BPR is placed by its BPR Status on the server (job_status.dept_clause),
// so department counts and lists come from /jobs?dept= and /jobs/stats/departments, not from these.
export const DEPT_STATUSES = {
  stores: { label: "Stores", statuses: ["", "pending"] },
  printing: { label: "Printing", statuses: ["in_production", "partially_printed", "printing"] },
  avt: { label: "AVT", statuses: ["printing", "flat_bed"] },
  slitting: { label: "Slitting & Numbering", statuses: ["avt"] },
  quality: { label: "Quality", statuses: ["slitting", "packing"] },
  packing: { label: "Packing", statuses: ["qc"] },
  dispatch: { label: "Dispatch", statuses: ["ready_to_dispatch"] },
};

/** Same translation StatusBadge uses, exposed for places (the status filter dropdown) that need
 * just the text, not the whole badge. */
export function readyForLabel(status) {
  const raw = status || "pending";
  const key = raw.trim().toLowerCase().replace(/\s+/g, "_");
  return READY_FOR_LABELS[key] ?? raw.replace(/_/g, " ");
}

/** A job the Jobs Pipeline sheet has no row of its own for (a Planning BPR): kept so production can work on
 * it, left out of the job counts until the team adds it to the Pipeline. */
export function PipelineTag({ job }) {
  if (!job?.not_in_pipeline) return null;
  return (
    <span className="inline-block px-1.5 py-0.5 rounded-sm border border-sky-300 bg-sky-50 text-sky-900 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap"
      title="No row of its own in the Jobs Pipeline sheet. Not counted in Total Jobs until it is added there." data-testid="pipeline-tag">
      Not in Pipeline
    </span>
  );
}

/** The operations head's priority call (job.urgent_schedule), shown the same way everywhere a job
 * appears outside the Scheduling Jobs tab itself — view-only here, same red/green as the dropdown
 * there that actually sets it. */
export function UrgentBadge({ urgent }) {
  return (
    <span
      data-testid="urgent-badge"
      className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide rounded-sm shrink-0 ${
        urgent ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"
      }`}
    >
      {urgent ? "Urgent" : "Scheduled"}
    </span>
  );
}

/** A job's number in its department's queue (job.queue_no, worked out on the server), so each desk and the
 * operations head see the same #1, #2, ... */
export function QueueNo({ n }) {
  if (n == null) return null;
  return <span data-testid="queue-no" className="font-mono-num text-xs font-semibold text-neutral-500 shrink-0">#{n}</span>;
}

/** A "Not in Pipeline" job not yet worked in the app: its status is the BPR Status sheet's stage name
 * ("Printing" = at Printing), not a stage the app has finished. */
export function excelStageStatus(job) {
  return !!job?.not_in_pipeline && !Object.values(job.stages || {}).some((s) => s?.completed_at);
}

export const stageKey = (v) => (v || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
// A BPR Status stage (where a job is) as the app status meaning "ready for" that department.
const AT_STAGE = {
  printing: "in_production", part_qty_printing: "in_production", partially_printed: "in_production",
  avt: "printing", part_qty_avt: "printing", flat_bed: "printing", flatbed: "printing",
  slitting: "avt", part_qty_slitting: "avt", numbering: "avt",
  qc: "slitting", quality: "slitting",
  packing: "qc", packing_fg: "qc", part_qty_packing: "qc",
  ready_to_dispatch: "ready_to_dispatch",
};
const IN_NO_DEPT = ["dispatched", "part_qty_disp", "on_hold", "hold", "only_shade_card_printed", "cancelled"];

/** The status a job's badge shows. A job the Pipeline marks Converted BPR reads "Ready for" the department
 * its BPR Status puts it at (Stores before it starts), as a job in the app's own flow does; past every
 * department (Dispatched, On Hold ...) it shows that BPR Status. A Not-in-Pipeline job's stage name too. */
export function badgeStatus(job, status = job?.status) {
  const key = stageKey(status);
  if (key === "converted_bpr") {
    const at = stageKey(job?.bpr_status);
    if (AT_STAGE[at]) return AT_STAGE[at];
    return IN_NO_DEPT.includes(at) ? job.bpr_status : "pending";
  }
  if (excelStageStatus(job) && AT_STAGE[key]) return AT_STAGE[key];
  return status;
}

export function StatusBadge({ status }) {
  const raw = status || "pending";
  const key = raw.trim().toLowerCase().replace(/\s+/g, "_");
  const label = READY_FOR_LABELS[key] ?? raw.replace(/_/g, " ");
  const preset = STATUS_STYLES[raw];
  // Fallback to legacy CSS class-based coloring for imported/legacy statuses
  const cls = preset || `status-${raw}`;
  return (
    <span
      data-testid={`status-badge-${raw}`}
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest rounded-sm ${cls}`}
    >
      {label}
    </span>
  );
}

// Anilox roller specs in use on the presses (LPC/CCM, or the sleeve's own code) — a fixed list,
// not job-driven like the other option combos, so it's hand-kept here rather than in options.py.
export const ANILOX_COMBOS = [
  "579/2.48", "579/2.325", "472/2.79", "472/2.48", "400/3", "400/2.5", "400/3.5",
  "339/4.65", "295/4.96", "260/5", "220/7", "180/8", "120/11", "79/15.5", "59/19.375",
  "160/9", "240/6", "300/4.2", "XS/3", "S/4", "C5/5", "M/6", "C7/7", "L/8", "C9/9",
  "C10/10", "XXL/13", "C18/18", "C2/2",
];

export const INK_MANUFACTURERS = ["SIEGWERK", "FLINT GROUP", "KS INKTECH", "UFLEX", "SERICOL", "MIXED", "FLURON"];

/** The Anilox Used table's 8 press-unit rows: Pantone always comes live from the job's own
 * colour_1..colour_8 (Planning) — never trusted from a stale save — while Anilox Details and
 * Ink Manufacturer are whatever was last entered (Printing fills them first; the Master Card
 * keeps them for the next repeat job). `stored` is the saved anilox_details array (partial rows
 * are fine); `colourSrc` is anything exposing colour_1..colour_8 (ctx.common, or the MJC form). */
export function aniloxRows(stored, colourSrc) {
  const src = Array.isArray(stored) ? stored : [];
  return Array.from({ length: 8 }, (_, i) => ({
    slot: i + 1,
    pantone_code: colourSrc?.[`colour_${i + 1}`] || "",
    anilox_combo: src[i]?.anilox_combo || "",
    ink_manufacturer: src[i]?.ink_manufacturer || "",
  }));
}
